import { Group, ModalData, Model, Prompt, PromptFunction } from '../Types';
import { AssistantTabs, ButtonVariant, DLPState, DocumentProcessingType } from '../Types/Enums';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from '../Store/Reducers';
import { setIsSending, setPrompts } from '../Store/Actions/chatActions';
import { setModal, setFileDlpModalState } from '../Store/Actions/modalActions';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import useGlobals from './useGlobals';
import { setSelectedPrompt } from '../Store/Actions/environmentActions';
import { promptService } from '../Services/promptService';
import { knowledgebaseService } from '../Services/knowledgebaseService';
import { groupService } from '../Services/groupService';
import { modelService } from '../Services/modelService';

const usePrompt = (userPrompt: boolean) => {
    const { handleNavigate, userSettings, windowWidth, i18n, demo, dispatch, settings, getPrompt, getBreakpoint } =
        useGlobals();
    const allPrompts = useSelector((state: RootState) => state.chats.prompts);
    const [prompt, setPrompt] = useState<Prompt>({
        identifier: '',
        name: '',
        description: '',
        value: settings.bestPracticeSystemPrompt || '',
        isUserPrompt: false,
        isBasePrompt: false,
        isVisionPrompt: false,
        hasVisionModel: false,
        canHandleDocuments: false,
        sort: 0,
        image: null,
        examples: [],
        files: [] as any[],
        proposedGlobal: false,
        dlp: DLPState.dlp_optional,
        model: '',
        functions: [] as string[],
        knowledgebases: [] as string[],
        combinedDlp: DLPState.dlp_optional,
        modelDlp: DLPState.dlp_optional,
        groups: [],
        documentProcessing: DocumentProcessingType.none,
    } as Prompt);

    const hasOpenAIDocumentProcessing = useCallback((currentPrompt: Prompt) => {
        return currentPrompt.documentProcessing === DocumentProcessingType.openai;
    }, []);

    const [loadedStates, setLoadedStates] = useState({
        promptLoaded: false,
        modelsLoaded: false,
        promptFunctionsLoaded: false,
    });
    const [chatId, setChatId] = useState<string>('');
    const [filesChangedModalOpen, setFilesChangedModalOpen] = useState<boolean>(false);
    const [filesInputKey, setFilesInputKey] = useState<number>(0);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isAssistantModel, setIsAssistantModel] = useState<boolean>(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [models, setModels] = useState<Model[]>([]);
    const [promptFunctions, setPromptFunctions] = useState<PromptFunction[]>([]);
    const [knowledgebases, setKnowledgebases] = useState<any>([]); // Adjust type as needed
    const [knowledgebaseTotal, setKnowledgebaseTotal] = useState<number>(0);
    const [promptSnapshot, setPromptSnapshot] = useState<Prompt>({ ...prompt } as Prompt);
    const filesRef = useRef(prompt.files);
    const lastClickedElementRef = useRef<HTMLElement | null>(null);
    const selectedLanguage = useSelector((state: any) => state.i18n.selectedLanguage); //Wird nur bei neuem Prompt in der init verwendet?
    let { promptIdentifier } = useParams();
    const [selectedTab, setSelectedTab] = useState<AssistantTabs>(AssistantTabs.main);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [customButtonArray, setCustomButtonArray] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [groups, setGroups] = useState<Group[]>([]);
    const systemPromptInputRef = useRef<HTMLTextAreaElement | null>(null);
    const [changedSections, setChangedSections] = useState<{ [key: string]: boolean }>({
        basicInfo: false,
        image: false,
        prompt: false,
        model: false,
        connector: false,
        functions: false,
        description: false,
        knowledgebase: false,
        dataProtection: false,
        groups: false,
    });
    const [tempTabState, setTempTabState] = useState<Partial<Prompt> | null>(null);

    // 140px = 100px (Header) + 40px Container Padding (Prompt.tsx AdminContainer) + 8px für den box shadow
    const boxHeightClasses = {
        desktop: 'lg:h-[calc(var(--app-height)-140px)] ',
        tablet: 'md:h-fit ',
        mobile: 'md:h-fit ',
        maxHeight: '',
    };

    const boxHeightClassesStr = Object.values(boxHeightClasses).join(' ');

    useEffect(() => {
        filesRef.current = prompt.files;
    }, [prompt.files]);

    const updatePrompt = useCallback((updates: Partial<Prompt>) => {
        setPrompt((prevPrompt) => ({
            ...prevPrompt,
            ...updates,
        }));
    }, []);

    const handlePromptFunctionCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelectedPrompts = event.target.checked
                ? [...prompt.functions, event.target.name]
                : prompt.functions.filter((promptFunction: string) => promptFunction !== event.target.name);
            updatePrompt({ functions: newSelectedPrompts as [] });
        },
        [updatePrompt, prompt.functions],
    );

    const handleKnowledgebaseCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelectedKnowledgebases = event.target.checked
                ? [...prompt.knowledgebases, event.target.name]
                : prompt.knowledgebases.filter((knowledgebase: string) => knowledgebase !== event.target.name);
            updatePrompt({ knowledgebases: newSelectedKnowledgebases as [] });
        },
        [updatePrompt, prompt.knowledgebases],
    );

    const handleLocalDocumentProcessingChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                updatePrompt({
                    documentProcessing: DocumentProcessingType.local,
                });
            } else {
                updatePrompt({
                    documentProcessing: DocumentProcessingType.none,
                });
            }
        },
        [updatePrompt],
    );

    const handleOpenAIDocumentProcessingChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                const currentModel = models.find((model) => model.identifier === prompt.model);
                if (!currentModel?.isAssistantModel) {
                    dispatch(
                        setModal({
                            state: 'open',
                            title: i18n.translate({ id: 'modelFunctionSwitch.title', sourceName: 'Prompt' }),
                            content: i18n.translate({ id: 'modelFunctionSwitch.description', sourceName: 'Prompt' }),
                            modalSize: 'small',
                            closeOnAction: true,
                            showCloseButton: false,
                            buttonRightAction: () => {
                                // Modal schließen
                                dispatch(setModal({ state: 'closed' }));
                            },
                            closeFunction: () => {
                                lastClickedElementRef.current = null;
                            },
                        }),
                    );
                    return;
                }

                const newFunctions = [...prompt.functions];
                if (!newFunctions.includes('file_search')) {
                    newFunctions.push('file_search');
                }

                updatePrompt({
                    documentProcessing: DocumentProcessingType.openai,
                    functions: newFunctions,
                });
            } else {
                const newFunctions = prompt.functions.filter((func) => func !== 'file_search');

                updatePrompt({
                    documentProcessing: DocumentProcessingType.none,
                    functions: newFunctions,
                });
            }
        },
        [updatePrompt, models, prompt.model, dispatch, i18n, prompt.functions],
    );

    const handleResetFunctions = useCallback(
        async (tempSelectedModel: string) => {
            updatePrompt({
                functions: prompt.functions.filter(
                    (promptFunction: string) =>
                        promptFunction !== 'code_interpreter' && promptFunction !== 'file_search',
                ),
            });

            updatePrompt({
                model: tempSelectedModel,
                files: [],
                documentProcessing:
                    prompt.documentProcessing === DocumentProcessingType.openai
                        ? DocumentProcessingType.none
                        : prompt.documentProcessing,
            });
            setIsAssistantModel(
                models.find((model) => model.identifier === tempSelectedModel)?.isAssistantModel || false,
            );
        },
        [models, updatePrompt, prompt.functions, prompt.documentProcessing],
    );

    const canChangeModel = useCallback(
        (modelIdentifier: string) => {
            return (
                models.find((model) => model.identifier === prompt.model)?.isAssistantModel &&
                !models.find((model) => model.identifier === modelIdentifier)?.isAssistantModel &&
                (prompt.functions.some(
                    (functionIdentifier) =>
                        functionIdentifier === 'code_interpreter' ||
                        (functionIdentifier === 'file_search' && hasOpenAIDocumentProcessing(prompt)),
                ) ||
                    (prompt.files.length > 0 && hasOpenAIDocumentProcessing(prompt)) ||
                    hasOpenAIDocumentProcessing(prompt))
            );
        },
        [models, hasOpenAIDocumentProcessing, prompt],
    );

    const checkAssistantModel = useCallback(
        (modelIdentifier: string) => {
            return models.find((model) => model.identifier === modelIdentifier)?.isAssistantModel || false;
        },
        [models],
    );

    const handleDropdownChange = useCallback(
        (modelIdentifier: string) => {
            if (
                models.find((model) => model.identifier === prompt.model)?.isAssistantModel &&
                !models.find((model) => model.identifier === modelIdentifier)?.isAssistantModel &&
                (prompt.functions.some(
                    (functionIdentifier) =>
                        functionIdentifier === 'code_interpreter' ||
                        (functionIdentifier === 'file_search' && hasOpenAIDocumentProcessing(prompt)),
                ) ||
                    (prompt.files.length > 0 && hasOpenAIDocumentProcessing(prompt)) ||
                    hasOpenAIDocumentProcessing(prompt))
            ) {
                dispatch(
                    setModal({
                        state: 'open',
                        title: i18n.translate({ id: 'modelFunctionReset.title', sourceName: 'Prompt' }),
                        content: i18n.translate({ id: 'modelFunctionReset.description', sourceName: 'Prompt' }),
                        modalSize: 'small',
                        closeOnAction: true,
                        showCloseButton: false,
                        buttonRightAction: () => {
                            handleResetFunctions(modelIdentifier);
                        },
                        closeFunction: () => {
                            lastClickedElementRef.current = null;
                        },
                    }),
                );
            } else {
                updatePrompt({
                    model: modelIdentifier,
                    modelDlp:
                        models.find((model) => model.identifier === modelIdentifier)?.dlp || DLPState.dlp_optional,
                });
                setIsAssistantModel(
                    models.find((model) => model.identifier === modelIdentifier)?.isAssistantModel || false,
                );
            }
        },
        [models, dispatch, i18n, handleResetFunctions, updatePrompt, hasOpenAIDocumentProcessing, prompt],
    );

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImagePreview(URL.createObjectURL(file));
                updatePrompt({ image: file });
            }
        },
        [updatePrompt],
    );

    const handleImageRemove = useCallback(() => {
        setImagePreview(null);
        updatePrompt({ image: null });
    }, [updatePrompt]);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                const maxFileSize = 64 * 1024 * 1024;

                try {
                    const modelResponse = await modelService.getModel(
                        prompt.model === '' || typeof prompt.model === 'undefined'
                            ? models[0].identifier
                            : prompt.model,
                    );

                    const lowerCaseAllowedMimeTypes = modelResponse.data?.allowedMimeTypes?.map((mimeType: string) =>
                        mimeType.toLowerCase(),
                    );

                    const uploadPromises = Array.from(e.target.files).map(async (blob) => {
                        if (!lowerCaseAllowedMimeTypes?.includes(blob.type.toLowerCase())) {
                            toast.error(
                                i18n.translate({
                                    id: 'fileUpload.error.fileType',
                                    sourceName: 'FileUpload',
                                    params: {
                                        fileName: blob.name,
                                    },
                                }),
                            );
                            return null;
                        }

                        if (blob.size > maxFileSize) {
                            toast.error(
                                i18n.translate({
                                    id: 'fileUpload.error.fileSize',
                                    sourceName: 'FileUpload',
                                    params: {
                                        fileName: blob.name,
                                    },
                                }),
                            );
                            return null;
                        }

                        const file = new File([blob], blob.name);
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append(
                            'modelIdentifier',
                            prompt.model === '' || typeof prompt.model === 'undefined'
                                ? models[0].identifier
                                : prompt.model,
                        );

                        try {
                            const response = await promptService.uploadFile(formData);
                            if (response.data.error) {
                                console.error(response.data.error);
                                toast.error(
                                    i18n.translate({
                                        id: 'fileUpload.error.upload',
                                        sourceName: 'FileUpload',
                                        params: {
                                            fileName: blob.name,
                                        },
                                    }),
                                );
                                dispatch(setIsSending(false));
                                return;
                            }
                            return {
                                name: response.data.name,
                                sha1: response.data.sha1,
                            };
                        } catch (error) {
                            toast.error(
                                i18n.translate({
                                    id: 'fileUpload.error.upload',
                                    sourceName: 'FileUpload',
                                    params: {
                                        fileName: blob.name,
                                    },
                                }),
                            );
                            console.error('File upload error:', error);
                            dispatch(setIsSending(false));
                            return null;
                        }
                    });

                    const results = await Promise.all(uploadPromises);
                    const successfulUploads = results.filter(
                        (result): result is { name: string; sha1: string } => result !== null,
                    );

                    if (successfulUploads.length > 0) {
                        dispatch(
                            setFileDlpModalState({
                                state: 'open',
                                files: successfulUploads,
                                combinedDlp: DLPState.dlp_optional,
                                callback: (files: any[]) => {
                                    updatePrompt({
                                        files: [...prompt.files, ...files],
                                    });
                                },
                            }),
                        );
                    }
                } catch (error) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.model',
                            sourceName: 'FileUpload',
                        }),
                    );
                    console.error('Model loading error:', error);
                }
            }
        },
        [prompt.model, models, dispatch, updatePrompt, prompt.files, i18n],
    );

    const handleFileClickModal = useCallback(
        (modalContent: ReactNode) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'modelFunctionReset.title', sourceName: 'Prompt' }),
                    content: modalContent,
                    modalSize: 'medium',
                    closeOnAction: false,
                    showCloseButton: true,
                    showRightButton: false,
                    showLeftButton: false,
                    closeFunction: () => {
                        lastClickedElementRef.current = null;
                    },
                }),
            );
        },
        [dispatch, i18n],
    );

    const closeFileClickModal = useCallback(() => {
        dispatch(
            setModal({
                state: 'closed',
                title: '',
                content: '',
                buttonRightText: null,
                buttonRightAction: null,
                buttonRightVariant: 'primary',
                showRightButton: true,
                buttonLeftText: null,
                buttonLeftAction: null,
                buttonLeftVariant: 'secondary',
                showLeftButton: true,
                modalSize: 'small',
                closeOnAction: false,
                showCloseButton: true,
            }),
        );
    }, [dispatch]);

    const handleFilesButtonClick = useCallback(() => {
        const fileInput = document.querySelector('#filesInput') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }, []);

    const removeFile = useCallback(
        (index: number) => {
            const newFiles = prompt.files.filter((_, i) => i !== index);
            updatePrompt({ files: newFiles });
            setFilesInputKey((prev) => prev + 1);
        },
        [prompt.files, updatePrompt],
    );

    const UpdatePromptList = useCallback(
        (newPrompt: Prompt) => {
            const updatedPrompts = allPrompts.some((p) => p.identifier === newPrompt.identifier)
                ? allPrompts.map((p) => (p.identifier === newPrompt.identifier ? newPrompt : p))
                : [...allPrompts, newPrompt];

            dispatch(setPrompts(updatedPrompts));
        },
        [allPrompts, dispatch],
    );

    const getOrCreateChat = useCallback(
        (promptIdentifier: string) => {
            if (demo && !prompt.isUserPrompt) {
                return;
            }
            promptService.getPreviewChat(promptIdentifier).then((response: any) => {
                if (response.data === null || response.data.success === false) {
                    promptService.createPreviewChat(promptIdentifier).then((response: any) => {
                        setChatId(response.data.identifier);
                    });
                } else {
                    setChatId(response.data.identifier);
                }
            });
        },
        [demo, prompt.isUserPrompt],
    );

    const handleSave = useCallback(() => {
        if (demo && !userPrompt) {
            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'Prompt' }));
            setIsSaving(false);
            return;
        }
        if (userSettings.isLoggedIn) {
            const loading =
                prompt.description === ''
                    ? promptIdentifier === 'new'
                        ? toast.loading(i18n.translate({ id: 'toast.create', sourceName: 'Prompt' }))
                        : toast.loading(i18n.translate({ id: 'toast.saveLoading', sourceName: 'Prompt' }))
                    : null;

            const data = new FormData();
            data.append('value', prompt.value ?? '');

            if (prompt.examples.length > 0) {
                prompt.examples.forEach((example) => {
                    data.append('examples[]', example);
                });
            }

            data.append('sort', (prompt.sort ?? 0).toString());

            if (prompt.image instanceof File || prompt.image instanceof Blob) {
                const fileType = prompt.image.type.split('/')[1];
                const fileName = `upload.${fileType}`;
                data.append('image', new File([prompt.image], fileName, { type: prompt.image.type }));
            }

            data.append('deleteImage', imagePreview === null ? '1' : '0');

            if (prompt.name !== undefined && prompt.name !== '') {
                data.append('name', prompt.name);
            }
            if (prompt.description !== undefined && prompt.description !== '') {
                data.append('description', prompt.description);
            }
            if (prompt.model !== undefined && prompt.model !== '') {
                data.append('model', prompt.model);
            }

            prompt.functions.forEach((promptFunction) => {
                data.append('promptFunctions[]', promptFunction);
            });
            // Dokumentenverarbeitung senden
            data.append('documentProcessing', prompt.documentProcessing);
            data.append('isUserPrompt', userPrompt ? '1' : '0');
            data.append('language', selectedLanguage);
            data.append('dlp', prompt.dlp);

            prompt.knowledgebases.forEach((knowledgebase) => {
                data.append('knowledgebases[]', knowledgebase);
            });

            prompt.groups &&
                prompt.groups.forEach((group) => {
                    data.append('groups[]', group);
                });

            prompt.files.forEach((file) => {
                if (file.sha1 === null || typeof file.sha1 === 'undefined') {
                    data.append('oldFiles[]', file.identifier);
                } else {
                    data.append('files[]', file.sha1);
                }
            });

            let urlPart;
            if (promptIdentifier === 'new') {
                urlPart = 'create';
            } else {
                urlPart = 'update/' + promptIdentifier;
            }
            setIsSaving(true);
            promptService.savePrompt(urlPart, data).then((response) => {
                loading && toast.dismiss(loading);
                if (promptIdentifier !== 'new') {
                    promptService.clearPreviewChat(chatId).then(() => {
                        setPrompt(response.data);
                        setPromptSnapshot(response.data);
                        UpdatePromptList(response.data);
                        dispatch(setSelectedPrompt(response.data.identifier));
                        getOrCreateChat(promptIdentifier ?? '');
                        toast.success(i18n.translate({ id: 'toast.success', sourceName: 'Prompt' }));
                        setIsSaving(false);
                        setHasChanges(false);
                    });
                } else {
                    UpdatePromptList(response.data);
                    dispatch(setSelectedPrompt(response.data.identifier));
                    toast.success(i18n.translate({ id: 'toast.success', sourceName: 'Prompt' }));
                    handleNavigate(
                        (userPrompt ? '/userassistants' : '/admin/assistants') + '/' + response.data.identifier,
                    );
                    setLoadedStates((prevState) => ({ ...prevState, promptLoaded: true }));
                    setIsSaving(false);
                    setHasChanges(false);
                }
            });
        }
    }, [
        chatId,
        demo,
        getOrCreateChat,
        handleNavigate,
        i18n,
        prompt,
        promptIdentifier,
        selectedLanguage,
        UpdatePromptList,
        userPrompt,
        userSettings.isLoggedIn,
        imagePreview,
        dispatch,
    ]);

    const addExample = useCallback(() => {
        if (prompt.examples.length < 4) {
            updatePrompt({ examples: [...prompt.examples, ''] });
        }
    }, [prompt.examples, updatePrompt]);

    const removeExample = useCallback(
        (index: number) => {
            updatePrompt({ examples: prompt.examples.filter((_, i) => i !== index) });
        },
        [prompt.examples, updatePrompt],
    );

    const handleExampleChange = useCallback(
        (index: number, value?: string) => {
            const updatedExamples = [...prompt.examples];
            updatedExamples[index] = value ?? '';
            updatePrompt({ examples: updatedExamples });
        },
        [prompt.examples, updatePrompt],
    );

    const handleFilesChangedCloseModal = useCallback(() => {
        console.log('handleFilesChangedCloseModal');
        dispatch(
            setModal({
                title: i18n.translate({ id: 'filesChanged.title', sourceName: 'Prompt' }),
                text: i18n.translate({ id: 'filesChanged.description', sourceName: 'Prompt' }),
                open: filesChangedModalOpen,
                onClose: () => {
                    dispatch(setModal({ state: 'closed' }));
                    setFilesChangedModalOpen(false);
                },
                closeLabel: i18n.translate({ id: 'filesChanged.close', sourceName: 'Prompt' }),
            } as ModalData),
        );
    }, [dispatch, filesChangedModalOpen, i18n]);

    const handleResetSystemPrompt = useCallback(
        (customButtons: any = null) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'resetSystemPrompt.title', sourceName: 'Prompt' }),
                    content: i18n.translate({ id: 'resetSystemPrompt.description', sourceName: 'Prompt' }),
                    modalSize: 'small',
                    showRightButton: false,
                    showLeftButton: false,
                    closeOnAction: true,
                    showCloseButton: true,
                    buttonRightText: i18n.translate({ id: 'resetSystemPrompt.confirm', sourceName: 'Prompt' }),
                    buttonRightAction: () => {
                        updatePrompt({ value: settings.bestPracticeSystemPrompt || '' });
                    },

                    buttonRightVariant: 'primary',
                    buttonLeftText: i18n.translate({ id: 'resetSystemPrompt.cancel', sourceName: 'Prompt' }),
                    buttonLeftVariant: 'secondary',
                    customButtons: [...customButtons],
                }),
            );
        },
        [dispatch, i18n, settings.bestPracticeSystemPrompt, updatePrompt],
    );

    const handleResetField = useCallback(
        (options: { title: string; content: any; resetFunction: () => void; noModal: boolean }) => {
            if (options.noModal) {
                options.resetFunction();
                return;
            }

            dispatch(
                setModal({
                    state: 'open',
                    title: options.title,
                    content: options.content,
                    modalSize: 'small',
                    closeOnAction: true,
                    showCloseButton: true,
                    buttonRightText: i18n.translate({ id: 'resetSystemPrompt.confirm', sourceName: 'Prompt' }),
                    buttonRightAction: () => options.resetFunction(),
                    buttonRightVariant: 'primary',
                    buttonLeftText: i18n.translate({ id: 'resetSystemPrompt.cancel', sourceName: 'Prompt' }),
                    buttonLeftVariant: 'secondary',
                }),
            );
        },
        [dispatch, i18n],
    );

    const computeChangedValues = useCallback((currentPrompt: Prompt, snapshotPrompt: Prompt) => {
        const changes: { [key: string]: boolean } = {
            basicInfo: false,
            prompt: false,
            model: false,
            connector: false,
            functions: false,
            description: false,
            knowledgebase: false,
            dataProtection: false,
            groups: false,
        };

        // Check for changes in each section
        changes.basicInfo = JSON.stringify(currentPrompt.name) !== JSON.stringify(snapshotPrompt.name);
        changes.image = JSON.stringify(currentPrompt.image) !== JSON.stringify(snapshotPrompt.image);
        changes.prompt = JSON.stringify(currentPrompt.value) !== JSON.stringify(snapshotPrompt.value);
        changes.model = JSON.stringify(currentPrompt.model) !== JSON.stringify(snapshotPrompt.model);
        changes.functions =
            JSON.stringify(currentPrompt.functions) !== JSON.stringify(snapshotPrompt.functions) ||
            JSON.stringify(currentPrompt.files) !== JSON.stringify(snapshotPrompt.files) ||
            JSON.stringify(currentPrompt.documentProcessing) !== JSON.stringify(snapshotPrompt.documentProcessing);
        changes.description =
            JSON.stringify(currentPrompt.description) !== JSON.stringify(snapshotPrompt.description) ||
            JSON.stringify(currentPrompt.examples) !== JSON.stringify(snapshotPrompt.examples);
        changes.knowledgebase =
            JSON.stringify(currentPrompt.knowledgebases) !== JSON.stringify(snapshotPrompt.knowledgebases);
        changes.dataProtection = JSON.stringify(currentPrompt.dlp) !== JSON.stringify(snapshotPrompt.dlp);
        changes.groups = JSON.stringify(currentPrompt.groups) !== JSON.stringify(snapshotPrompt.groups);

        return changes;
    }, []);

    const hasFunctionChanges = useCallback(() => {
        const standardFunctions =
            prompt?.functions?.some(
                (f) => (f === 'code_interpreter' || f === 'file_search') && !promptSnapshot?.functions?.includes(f),
            ) ||
            promptSnapshot?.functions?.some(
                (f) => (f === 'code_interpreter' || f === 'file_search') && !prompt.functions.includes(f),
            );

        const filesChanged = JSON.stringify(prompt.files) !== JSON.stringify(promptSnapshot.files);

        return standardFunctions || filesChanged;
    }, [prompt, promptSnapshot]);

    const hasConnectorChanges = useCallback(() => {
        return (
            (prompt?.functions.length > 0 &&
                prompt?.functions?.some(
                    (f) => f !== 'code_interpreter' && f !== 'file_search' && !promptSnapshot.functions.includes(f),
                )) ||
            promptSnapshot?.functions?.some(
                (f) => f !== 'code_interpreter' && f !== 'file_search' && !prompt?.functions?.includes(f),
            )
        );
    }, [prompt, promptSnapshot]);

    const openTab = useCallback(
        (tab: AssistantTabs) => {
            if (tab !== AssistantTabs.main) {
                setTempTabState(JSON.parse(JSON.stringify(prompt)));
            }
            setSelectedTab(tab);
        },
        [prompt],
    );

    const closeTabWithoutSaving = useCallback(() => {
        if (tempTabState) {
            setPrompt(tempTabState as Prompt);
        }
        setTempTabState(null);
        setSelectedTab(AssistantTabs.main);
    }, [tempTabState]);

    const closeTabWithSaving = useCallback(() => {
        setTempTabState(null);
        setSelectedTab(AssistantTabs.main);
    }, []);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (promptIdentifier !== 'new' && promptIdentifier !== undefined) {
            promptService.getPrompt(userPrompt, promptIdentifier).then(async (response: any) => {
                if (!userPrompt) {
                    groupService.getGroups().then((groupResponse: any) => {
                        setGroups(groupResponse.data.data);
                    });
                }

                setPrompt(response.data);
                setPromptSnapshot(response.data);
                setImagePreview(response.data.image);
                setSelectedPrompt(response.data.identifier);
                getOrCreateChat(promptIdentifier ?? '');
                setLoadedStates((prevState) => ({ ...prevState, promptLoaded: true }));
            });
        } else {
            if (!userPrompt) {
                groupService.getGroups().then((groupResponse: any) => {
                    setGroups(groupResponse.data.data);
                    setLoadedStates((prevState) => ({ ...prevState, promptLoaded: true }));
                });
            }
            setLoadedStates((prevState) => ({ ...prevState, promptLoaded: true }));
        }

        if (!userPrompt && (userSettings.isAdmin || userSettings.isSuperAdmin || demo)) {
            promptService.getPromptFunctions(false).then((response: any) => {
                setPromptFunctions(response.data.data);
                setLoadedStates((prevState) => ({ ...prevState, promptFunctionsLoaded: true }));
            });

            knowledgebaseService.getKnowledgebasesByPage(1, 10).then((response: any) => {
                setKnowledgebases(response.data.data);
                setKnowledgebaseTotal(response.data.total);
            });
        } else {
            promptService.getPromptFunctions(true).then((response: any) => {
                setPromptFunctions(response.data.data);
                setLoadedStates((prevState) => ({ ...prevState, promptFunctionsLoaded: true }));
            });
        }
    }, [
        userSettings.isLoggedIn,
        promptIdentifier,
        userSettings.isAdmin,
        userSettings.isSuperAdmin,
        demo,
        userPrompt,
        getOrCreateChat,
    ]);

    useEffect(() => {
        if (userSettings.isLoggedIn && loadedStates.promptLoaded) {
            promptService.getModels(userPrompt).then((response: any) => {
                setModels(response.data);
                setLoadedStates((prevState) => ({ ...prevState, modelsLoaded: true }));
            });
        }
    }, [userSettings.isLoggedIn, loadedStates.promptLoaded, userPrompt, settings.defaultModel]);

    useEffect(() => {
        if (models.length > 0) {
            if (prompt.model === '' || typeof prompt.model === 'undefined') {
                const defaultModel = models.find((model) => model.identifier === settings.defaultModel)
                    ? settings.defaultModel
                    : models[0].identifier;
                updatePrompt({ model: defaultModel });
                if (promptSnapshot.model === '' || typeof promptSnapshot.model === 'undefined') {
                    setPromptSnapshot({ ...promptSnapshot, model: defaultModel });
                }
                setIsAssistantModel(models[0].isAssistantModel);
            } else {
                setIsAssistantModel(
                    models.find((model) => model.identifier === prompt.model)?.isAssistantModel || false,
                );
            }
        }
    }, [models, prompt.model, prompt, updatePrompt, promptSnapshot, settings.defaultModel]);

    useEffect(() => {
        if (lastClickedElementRef.current) lastClickedElementRef?.current.click();
        lastClickedElementRef.current = null;
        let hasFiles = false;
        if (filesRef.current.length > 0 && models.length > 0) {
            const data = new URLSearchParams();
            filesRef.current.forEach((file) => {
                if (file.sha1) {
                    data.append('files[]', file.sha1);
                    hasFiles = true;
                } else {
                    updatePrompt({ files: filesRef.current.filter((f: any) => f.name !== file.name) });
                }
            });
            data.append(
                'modelIdentifier',
                prompt.model === '' || typeof prompt.model === 'undefined' ? models[0].identifier : prompt.model,
            );

            if (hasFiles) {
                promptService.checkUploadedFiles(data).then((response: any) => {
                    if (response.data.error) {
                        toast.error(response.data.error);
                        dispatch(setIsSending(false));
                        return;
                    }

                    let changedFiles = filesRef.current.filter((file) => {
                        return !response.data.some((f: any) => f.name === file.name);
                    });

                    if (changedFiles.length > 0) {
                        handleFilesChangedCloseModal();
                    }

                    const changedFilesInPrompt = promptSnapshot.files.filter((file: any) => {
                        return !response.data.some((f: any) => f.sha1 === file.sha1);
                    });

                    if (changedFilesInPrompt.length > 0) {
                        updatePrompt({ files: response.data });
                        setPromptSnapshot({ ...promptSnapshot, files: response.data });
                    }
                });
            }
        }
    }, [models, prompt.model, filesRef, dispatch, updatePrompt, promptSnapshot, handleFilesChangedCloseModal]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.getElementById('imageDropdown');
            const cameraIcon = document.getElementById('cameraIcon');
            if (
                dropdown &&
                !dropdown.contains(event.target as Node) &&
                cameraIcon &&
                !cameraIcon.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        if (Object.keys(promptSnapshot).length > 0) {
            const changes = computeChangedValues(prompt, promptSnapshot);
            setChangedSections(changes);
            setHasChanges(Object.values(changes).some((value) => value));
        }
    }, [prompt, promptSnapshot, computeChangedValues]);

    useEffect(() => {
        if (Object.keys(promptSnapshot).length > 0) {
            const buttons = [
                {
                    type: 'cancel',
                    variant: ButtonVariant.secondary,
                    text: i18n.translate({ id: 'systemPromptModal.cancel', sourceName: 'Prompt' }),
                },
                ...(changedSections.prompt
                    ? [
                          {
                              type: 'reset',
                              configKey: 'systemPrompt',
                              variant: ButtonVariant.primary,
                              text: i18n.translate({ id: 'systemPromptModal.original', sourceName: 'Prompt' }),
                          },
                      ]
                    : []),
                {
                    type: 'bestPractice',
                    variant: ButtonVariant.primary,
                    text: i18n.translate({ id: 'systemPromptModal.bestPractice', sourceName: 'Prompt' }),
                },
            ];

            setCustomButtonArray(buttons);
        }
    }, [changedSections, prompt, promptSnapshot, i18n]);

    const PromptActions = useMemo(
        () => ({
            addExample,
            closeFileClickModal,
            handleDropdownChange,
            handleExampleChange,
            handleFileChange,
            handleFileClickModal,
            handleFilesButtonClick,
            handleFilesChangedCloseModal,
            handleImageChange,
            handleImageRemove,
            handlePromptFunctionCheckboxChange,
            handleKnowledgebaseCheckboxChange,
            handleLocalDocumentProcessingChange,
            handleOpenAIDocumentProcessingChange,
            handleResetFunctions,
            handleResetSystemPrompt,
            handleSave,
            removeExample,
            removeFile,
            updatePrompt,
            setSelectedTab,
            getPrompt,
            handleResetField,
            setImagePreview,
            canChangeModel,
            checkAssistantModel,
            setCustomButtonArray,
            hasFunctionChanges,
            hasConnectorChanges,
            getBreakpoint,
            openTab,
            closeTabWithoutSaving,
            closeTabWithSaving,
        }),
        [
            addExample,
            closeFileClickModal,
            handleDropdownChange,
            handleExampleChange,
            handleFileChange,
            handleFileClickModal,
            handleFilesButtonClick,
            handleFilesChangedCloseModal,
            handleImageChange,
            handleImageRemove,
            handlePromptFunctionCheckboxChange,
            handleKnowledgebaseCheckboxChange,
            handleLocalDocumentProcessingChange,
            handleOpenAIDocumentProcessingChange,
            handleResetFunctions,
            handleResetSystemPrompt,
            handleSave,
            removeExample,
            removeFile,
            updatePrompt,
            setSelectedTab,
            getPrompt,
            handleResetField,
            setImagePreview,
            canChangeModel,
            checkAssistantModel,
            setCustomButtonArray,
            hasFunctionChanges,
            hasConnectorChanges,
            getBreakpoint,
            openTab,
            closeTabWithoutSaving,
            closeTabWithSaving,
        ],
    );

    return {
        boxHeightClassesStr,
        chatId,
        filesChangedModalOpen,
        filesInputKey,
        handleNavigate,
        i18n,
        imagePreview,
        isAssistantModel,
        lastClickedElementRef,
        loadedStates,
        models,
        prompt,
        PromptActions,
        promptFunctions,
        promptIdentifier,
        promptSnapshot,
        setIsAssistantModel,
        userPrompt,
        knowledgebases,
        knowledgebaseTotal,
        selectedTab,
        hasChanges,
        changedSections,
        bestPracticeSystemPrompt: settings.bestPracticeSystemPrompt,
        customButtonArray,
        isSaving,
        windowWidth,
        groups,
        systemPromptInputRef,
        demo,
    };
};

export default usePrompt;
